import * as THREE from 'three'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import vehicles from '../../content/vehicles.js'

export default class Car
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.objects = _options.objects
        this.physics = _options.physics
        this.shadows = _options.shadows
        this.materials = _options.materials
        this.controls = _options.controls
        this.sounds = _options.sounds
        this.renderer = _options.renderer
        this.camera = _options.camera
        this.debug = _options.debug
        this.config = _options.config
        this.vehicle = _options.vehicle

        // Set up
        this.container = new THREE.Object3D()
        this.position = new THREE.Vector3()

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('car')
            // this.debugFolder.open()
        }

        this.setModels()
        this.setMovement()
        this.setChassis()
        this.setAntena()
        this.setBackLights()
        this.setWheels()
        this.setTransformControls()
        this.setKlaxon()
    }

    setModels()
    {
        // Garajdan seçilen araç (src/content/vehicles.js); geçersizse ilk araç
        if(!vehicles.find((_vehicle) => _vehicle.id === this.vehicle))
        {
            this.vehicle = vehicles[0].id
        }

        const prefix = `car${this.vehicle.substring(0, 1).toUpperCase()}${this.vehicle.substring(1)}`

        this.models = {}
        this.models.chassis = this.resources.items[`${prefix}Chassis`]
        this.models.antena = this.resources.items[`${prefix}Antena`]
        this.models.backLightsBrake = this.resources.items[`${prefix}BackLightsBrake`]
        this.models.backLightsReverse = this.resources.items[`${prefix}BackLightsReverse`]
        this.models.wheel = this.resources.items[`${prefix}Wheel`]
    }

    // Araç modelleri kalıcı kapların içinde durur: değişince yalnız içerik yenilenir, tick'ler aynı kalır.
    // duplicated: kaynak sahneden taşımak yerine kopyalar (aynı araca geri dönülebilsin)
    convert(_model)
    {
        const model = this.objects.getConvertedMesh(_model.scene.children, { duplicated: true })

        // Açılış düşüşünden muaf (revealDrop.glsl aReveal.w = 2): araba zaten fizikle 12 m'den düşer
        model.traverse((_child) =>
        {
            if(_child.isMesh && !_child.geometry.attributes.aReveal)
            {
                const reveal = new Float32Array(_child.geometry.attributes.position.count * 4)
                for(let i = 3; i < reveal.length; i += 4)
                {
                    reveal[i] = 2
                }
                _child.geometry.setAttribute('aReveal', new THREE.BufferAttribute(reveal, 4))
            }
        })

        return model
    }

    buildChassis()
    {
        if(this.chassis.model)
        {
            this.chassis.object.remove(this.chassis.model)
        }
        this.chassis.model = this.convert(this.models.chassis)
        this.chassis.object.add(this.chassis.model)
    }

    buildAntena()
    {
        if(this.antena.model)
        {
            this.antena.object.remove(this.antena.model)
        }
        // getConvertedMesh kabı pivota (center) konur; pivot kalıcı kaba taşınır, sallanma onun etrafında olur
        this.antena.model = this.convert(this.models.antena)
        this.antena.object.position.copy(this.antena.model.position)
        this.antena.model.position.set(0, 0, 0)
        this.antena.object.add(this.antena.model)
    }

    buildBackLights()
    {
        for(const _lights of [this.backLightsBrake, this.backLightsReverse])
        {
            if(_lights.model)
            {
                _lights.object.remove(_lights.model)
            }
        }

        this.backLightsBrake.model = this.convert(this.models.backLightsBrake)
        this.backLightsReverse.model = this.convert(this.models.backLightsReverse)

        for(const _lights of [this.backLightsBrake, this.backLightsReverse])
        {
            _lights.model.traverse((_child) =>
            {
                if(_child instanceof THREE.Mesh)
                {
                    _child.material = _lights.material
                }
            })
            _lights.object.add(_lights.model)
        }
    }

    buildWheels()
    {
        const model = this.convert(this.models.wheel)
        model.position.set(0, 0, 0)

        for(const _item of this.wheels.items)
        {
            if(_item.userData.model)
            {
                _item.remove(_item.userData.model)
            }
            _item.userData.model = model.clone()
            _item.add(_item.userData.model)
        }
    }

    setVehicle(_id)
    {
        this.vehicle = _id
        this.setModels()
        this.buildChassis()
        this.buildAntena()
        this.buildBackLights()
        this.buildWheels()
    }

    setMovement()
    {
        this.movement = {}
        this.movement.speed = new THREE.Vector3()
        this.movement.localSpeed = new THREE.Vector3()
        this.movement.acceleration = new THREE.Vector3()
        this.movement.localAcceleration = new THREE.Vector3()
        this.movement.lastScreech = 0

        // Time tick
        this.time.on('tick', () =>
        {
            // Movement
            const movementSpeed = new THREE.Vector3()
            movementSpeed.copy(this.chassis.object.position).sub(this.chassis.oldPosition)
            movementSpeed.multiplyScalar(1 / this.time.delta * 17)
            this.movement.acceleration = movementSpeed.clone().sub(this.movement.speed)
            this.movement.speed.copy(movementSpeed)

            this.movement.localSpeed = this.movement.speed.clone().applyAxisAngle(new THREE.Vector3(0, 0, 1), - this.chassis.object.rotation.z)
            this.movement.localAcceleration = this.movement.acceleration.clone().applyAxisAngle(new THREE.Vector3(0, 0, 1), - this.chassis.object.rotation.z)

            // Sound
            this.sounds.engine.speed = this.movement.localSpeed.x
            this.sounds.engine.acceleration = this.controls.actions.up ? (this.controls.actions.boost ? 1 : 0.5) : 0

            if(this.movement.localAcceleration.x > 0.03 && this.time.elapsed - this.movement.lastScreech > 5000)
            {
                this.movement.lastScreech = this.time.elapsed
                this.sounds.play('screech')
            }
        })
    }

    setChassis()
    {
        this.chassis = {}
        this.chassis.offset = new THREE.Vector3(0, 0, - 0.28)
        this.chassis.object = new THREE.Object3D()
        this.buildChassis()
        this.chassis.object.position.copy(this.physics.car.chassis.body.position)
        this.chassis.oldPosition = this.chassis.object.position.clone()
        this.container.add(this.chassis.object)

        this.shadows.add(this.chassis.object, { sizeX: 3, sizeY: 2, offsetZ: 0.2, revealExempt: true })

        // Time tick
        this.time.on('tick', () =>
        {
            // Save old position for movement calculation
            this.chassis.oldPosition = this.chassis.object.position.clone()

            // Update if mode physics
            if(!this.transformControls.enabled)
            {
                this.chassis.object.position.copy(this.physics.car.chassis.body.position).add(this.chassis.offset)
                this.chassis.object.quaternion.copy(this.physics.car.chassis.body.quaternion)
            }

            // Update position
            this.position.copy(this.chassis.object.position)
        })
    }

    setAntena()
    {
        this.antena = {}

        this.antena.speedStrength = 10
        this.antena.damping = 0.035
        this.antena.pullBackStrength = 0.02

        this.antena.object = new THREE.Object3D()
        this.buildAntena()
        this.chassis.object.add(this.antena.object)

        this.antena.speed = new THREE.Vector2()
        this.antena.absolutePosition = new THREE.Vector2()
        this.antena.localPosition = new THREE.Vector2()

        // Time tick
        this.time.on('tick', () =>
        {
            const max = 1
            const accelerationX = Math.min(Math.max(this.movement.acceleration.x, - max), max)
            const accelerationY = Math.min(Math.max(this.movement.acceleration.y, - max), max)

            this.antena.speed.x -= accelerationX * this.antena.speedStrength
            this.antena.speed.y -= accelerationY * this.antena.speedStrength

            const position = this.antena.absolutePosition.clone()
            const pullBack = position.negate().multiplyScalar(position.length() * this.antena.pullBackStrength)
            this.antena.speed.add(pullBack)

            this.antena.speed.x *= 1 - this.antena.damping
            this.antena.speed.y *= 1 - this.antena.damping

            this.antena.absolutePosition.add(this.antena.speed)

            this.antena.localPosition.copy(this.antena.absolutePosition)
            this.antena.localPosition.rotateAround(new THREE.Vector2(), - this.chassis.object.rotation.z)

            this.antena.object.rotation.y = this.antena.localPosition.x * 0.1
            this.antena.object.rotation.x = this.antena.localPosition.y * 0.1
        })

        // Debug
        if(this.debug)
        {
            const folder = this.debugFolder.addFolder('antena')
            folder.open()

            folder.add(this.antena, 'speedStrength').step(0.001).min(0).max(50)
            folder.add(this.antena, 'damping').step(0.0001).min(0).max(0.1)
            folder.add(this.antena, 'pullBackStrength').step(0.0001).min(0).max(0.1)
        }
    }

    setBackLights()
    {
        this.backLightsBrake = {}

        this.backLightsBrake.material = this.materials.pures.items.red.clone()
        this.backLightsBrake.material.transparent = true
        this.backLightsBrake.material.opacity = 0.5

        this.backLightsBrake.object = new THREE.Object3D()
        this.chassis.object.add(this.backLightsBrake.object)

        // Back lights brake
        this.backLightsReverse = {}

        this.backLightsReverse.material = this.materials.pures.items.yellow.clone()
        this.backLightsReverse.material.transparent = true
        this.backLightsReverse.material.opacity = 0.5

        this.backLightsReverse.object = new THREE.Object3D()
        this.chassis.object.add(this.backLightsReverse.object)

        this.buildBackLights()

        // Time tick
        this.time.on('tick', () =>
        {
            this.backLightsBrake.material.opacity = this.physics.controls.actions.brake ? 1 : 0.5
            this.backLightsReverse.material.opacity = this.physics.controls.actions.down ? 1 : 0.5
        })
    }

    setWheels()
    {
        this.wheels = {}
        this.wheels.items = []

        for(let i = 0; i < 4; i++)
        {
            const object = new THREE.Object3D()

            this.wheels.items.push(object)
            this.container.add(object)
        }

        this.buildWheels()

        // Time tick
        this.time.on('tick', () =>
        {
            if(!this.transformControls.enabled)
            {
                for(const _wheelKey in this.physics.car.wheels.bodies)
                {
                    const wheelBody = this.physics.car.wheels.bodies[_wheelKey]
                    const wheelObject = this.wheels.items[_wheelKey]

                    wheelObject.position.copy(wheelBody.position)
                    wheelObject.quaternion.copy(wheelBody.quaternion)
                }
            }
        })
    }

    setTransformControls()
    {
        this.transformControls = new TransformControls(this.camera.instance, this.renderer.domElement)
        this.transformControls.size = 0.5
        this.transformControls.attach(this.chassis.object)
        this.transformControls.enabled = false
        this.transformControls.visible = this.transformControls.enabled

        document.addEventListener('keydown', (_event) =>
        {
            if(this.mode === 'transformControls')
            {
                if(_event.key === 'r')
                {
                    this.transformControls.setMode('rotate')
                }
                else if(_event.key === 'g')
                {
                    this.transformControls.setMode('translate')
                }
            }
        })

        this.transformControls.addEventListener('dragging-changed', (_event) =>
        {
            this.camera.orbitControls.enabled = !_event.value
        })

        this.container.add(this.transformControls)

        if(this.debug)
        {
            const folder = this.debugFolder.addFolder('controls')
            folder.open()

            folder.add(this.transformControls, 'enabled').onChange(() =>
            {
                this.transformControls.visible = this.transformControls.enabled
            })
        }
    }

    setKlaxon()
    {
        this.klaxon = {}
        this.klaxon.lastTime = this.time.elapsed

        window.addEventListener('keydown', (_event) =>
        {
            // Play horn sound
            if(_event.code === 'KeyH')
            {
                if(this.time.elapsed - this.klaxon.lastTime > 400)
                {
                    this.physics.car.jump(false, 150)
                    this.klaxon.lastTime = this.time.elapsed
                }

                this.sounds.play(Math.random() < 0.002 ? 'carHorn2' : 'carHorn1')
            }

            // Rain horns
            if(_event.key === 'k')
            {
                const x = this.position.x + (Math.random() - 0.5) * 3
                const y = this.position.y + (Math.random() - 0.5) * 3
                const z = 6 + 2 * Math.random()

                this.objects.add({
                    base: this.resources.items.hornBase.scene,
                    collision: this.resources.items.hornCollision.scene,
                    offset: new THREE.Vector3(x, y, z),
                    rotation: new THREE.Euler(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2),
                    duplicated: true,
                    shadow: { sizeX: 1.5, sizeY: 1.5, offsetZ: - 0.15, alpha: 0.35 },
                    mass: 5,
                    soundName: 'horn',
                    sleep: false
                })
            }
        })
    }
}
